import type { ReviewEntryTableRow } from '../types/reviewEntryTableRow'

export const DEFAULT_REVIEW_ENTRIES_SORTED_COLUMN_ID = '-date'

const DOCUMENT_LEVEL_SORT_COLUMNS = ['date', 'supplierName'] as const
const NUMERIC_SORT_COLUMNS = ['amountExclVat', 'amountInclVat', 'vatAmount'] as const

type DocumentLevelSortColumn = (typeof DOCUMENT_LEVEL_SORT_COLUMNS)[number]
type NumericSortColumn = (typeof NUMERIC_SORT_COLUMNS)[number]
type ReviewEntriesSortValue = string | number | undefined

const getDocumentGroups = (rows: ReviewEntryTableRow[]): ReviewEntryTableRow[][] => {
  const groups: ReviewEntryTableRow[][] = []
  const groupIndexByEntryId = new Map<string, number>()

  for (const row of rows) {
    const existingIndex = groupIndexByEntryId.get(row.entryId)

    if (existingIndex === undefined) {
      groupIndexByEntryId.set(row.entryId, groups.length)
      groups.push([row])
      continue
    }

    groups[existingIndex].push(row)
  }

  return groups
}

const getHeaderRow = (groupRows: ReviewEntryTableRow[]): ReviewEntryTableRow | undefined =>
  groupRows.find((row) => row.showDocumentLevelFields)

const getFirstDefinedValue = <T>(
  groupRows: ReviewEntryTableRow[],
  getValue: (row: ReviewEntryTableRow) => T | undefined,
): T | undefined => {
  for (const row of groupRows) {
    const value = getValue(row)

    if (value === undefined || value === '') {
      continue
    }

    return value
  }

  return undefined
}

const isDocumentLevelSortColumn = (columnId: string): columnId is DocumentLevelSortColumn =>
  (DOCUMENT_LEVEL_SORT_COLUMNS as readonly string[]).includes(columnId)

const isNumericSortColumn = (columnId: string): columnId is NumericSortColumn =>
  (NUMERIC_SORT_COLUMNS as readonly string[]).includes(columnId)

const getGroupSortValue = (groupRows: ReviewEntryTableRow[], columnId: string): ReviewEntriesSortValue => {
  if (isDocumentLevelSortColumn(columnId)) {
    return getHeaderRow(groupRows)?.[columnId]
  }

  if (columnId === 'account') {
    return getFirstDefinedValue(groupRows, (row) => row.account)
  }

  if (columnId === 'vatRate') {
    return getFirstDefinedValue(groupRows, (row) => row.vatRate)
  }

  if (columnId === 'amountInclVat') {
    return getHeaderRow(groupRows)?.amountInclVat ?? getFirstDefinedValue(groupRows, (row) => row.amountInclVat)
  }

  if (isNumericSortColumn(columnId)) {
    return getFirstDefinedValue(groupRows, (row) => row[columnId])
  }

  return undefined
}

const compareSortValues = (left: ReviewEntriesSortValue, right: ReviewEntriesSortValue): number => {
  const leftMissing = left === undefined || left === ''
  const rightMissing = right === undefined || right === ''

  if (leftMissing && rightMissing) {
    return 0
  }

  if (leftMissing) {
    return 1
  }

  if (rightMissing) {
    return -1
  }

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }

  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

export const sortReviewEntriesTableRows = (
  rows: ReviewEntryTableRow[],
  sortedColumnId: string,
): ReviewEntryTableRow[] => {
  if (!sortedColumnId) {
    return rows
  }

  const isDesc = sortedColumnId.startsWith('-')
  const columnId = isDesc ? sortedColumnId.slice(1) : sortedColumnId
  const direction = isDesc ? -1 : 1

  return getDocumentGroups(rows)
    .slice()
    .sort((leftGroup, rightGroup) => {
      const comparison = compareSortValues(
        getGroupSortValue(leftGroup, columnId),
        getGroupSortValue(rightGroup, columnId),
      )

      return comparison * direction
    })
    .flat()
}
